import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { PageHeader, PrimaryButton, GhostButton, GlassCard } from "@/components/Primitives";
import RangeSelect, { DEFAULT_RANGE } from "@/components/RangeSelect";
import ExportButton from "@/components/ExportButton";
import Modal, { inputCls, Field } from "@/components/Modal";
import { Plus, X, Upload, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";

const REVENUE_TYPES = ["Project Revenue", "Hosting Revenue", "Maintenance Revenue", "Consulting Revenue", "Other"];
const EXPENSE_CATS = ["Hosting", "Software", "Advertising", "Contractors", "Domains", "APIs", "Business Expenses", "Other"];
const REV_STATUS = ["received", "pending"];

const blankRev = { client_id: "", project_id: "", revenue_type: "Project Revenue", amount: 0, status: "received", notes: "", date: "" };
const blankExp = { expense_name: "", category: "Software", vendor: "", amount: 0, notes: "", date: "", receipt_path: null };

function RevenueDialog({ open, initial, clients, projects, onClose, onSaved }) {
  const [form, setForm] = useState(blankRev);
  useEffect(() => {
    if (open) setForm(initial ? { ...blankRev, ...initial, date: (initial.date || "").slice(0, 10) } : blankRev);
  }, [open, initial]);
  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: Number(form.amount) || 0, date: form.date || null, client_id: form.client_id || null, project_id: form.project_id || null };
      if (isEdit) await api.patch(`/revenue/${initial.id}`, payload);
      else await api.post("/revenue", payload);
      toast.success(isEdit ? "Saved" : "Created");
      onClose(); onSaved();
    } catch (err) { toast.error(formatErr(err)); }
  };
  const confirm = useConfirm();
  const remove = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: "Delete this revenue entry?",
      description: "Removing this entry will adjust your totals and dashboards.",
      confirmLabel: "Delete revenue",
    });
    if (!ok) return;
    try { await api.delete(`/revenue/${initial.id}`); toast.success("Deleted"); onClose(); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={submit} className="p-8" data-testid="revenue-dialog">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-light">{isEdit ? "Edit revenue" : "New revenue"}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Client">
            <select className={inputCls} value={form.client_id || ""} onChange={u("client_id")} data-testid="rev-client">
              <option value="">— None —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </Field>
          <Field label="Project">
            <select className={inputCls} value={form.project_id || ""} onChange={u("project_id")}>
              <option value="">— None —</option>
              {projects.filter((p) => !form.client_id || p.client_id === form.client_id).map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select className={inputCls} value={form.revenue_type} onChange={u("revenue_type")}>
                {REVENUE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className={inputCls} value={form.status} onChange={u("status")}>
                {REV_STATUS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount"><input type="number" required className={inputCls} value={form.amount} onChange={u("amount")} data-testid="rev-amount" /></Field>
            <Field label="Date"><input type="date" className={inputCls} value={form.date} onChange={u("date")} /></Field>
          </div>
          <Field label="Notes"><textarea rows={2} className={inputCls} value={form.notes || ""} onChange={u("notes")} /></Field>
        </div>
        <div className="flex items-center justify-between mt-8">
          {isEdit ? (
            <button type="button" onClick={remove} className="text-rose-400 text-sm inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton type="submit" data-testid="rev-submit">{isEdit ? "Save" : "Add"}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}

function ExpenseDialog({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(blankExp);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (open) { setForm(initial ? { ...blankExp, ...initial, date: (initial.date || "").slice(0, 10) } : blankExp); setFile(null); }
  }, [open, initial]);
  const u = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    try {
      let receipt_path = form.receipt_path || null;
      if (file) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_path = data.path;
        setUploading(false);
      }
      const payload = { ...form, amount: Number(form.amount) || 0, date: form.date || null, receipt_path };
      if (isEdit) await api.patch(`/expenses/${initial.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(isEdit ? "Saved" : "Created");
      onClose(); onSaved();
    } catch (err) { setUploading(false); toast.error(formatErr(err)); }
  };
  const confirm = useConfirm();
  const remove = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: "Delete this expense?",
      description: "Removing this entry will adjust your totals and dashboards.",
      confirmLabel: "Delete expense",
    });
    if (!ok) return;
    try { await api.delete(`/expenses/${initial.id}`); toast.success("Deleted"); onClose(); onSaved(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={submit} className="p-8" data-testid="expense-dialog">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-light">{isEdit ? "Edit expense" : "New expense"}</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <Field label="Name"><input required className={inputCls} value={form.expense_name} onChange={u("expense_name")} data-testid="exp-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select className={inputCls} value={form.category} onChange={u("category")} data-testid="exp-category">
                {EXPENSE_CATS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Vendor"><input className={inputCls} value={form.vendor || ""} onChange={u("vendor")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount"><input type="number" required className={inputCls} value={form.amount} onChange={u("amount")} data-testid="exp-amount" /></Field>
            <Field label="Date"><input type="date" className={inputCls} value={form.date} onChange={u("date")} /></Field>
          </div>
          <Field label="Receipt">
            <label className="flex items-center gap-3 px-3 py-2.5 bg-black/40 border border-dashed border-white/15 rounded-xl cursor-pointer hover:border-[#007AFF]/40">
              <Upload className="w-4 h-4 text-white/50" />
              <span className="text-sm text-white/60">
                {file ? file.name : (form.receipt_path ? "Receipt on file — replace?" : "Upload receipt (PDF, JPG, PNG)")}
              </span>
              <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </Field>
          <Field label="Notes"><textarea rows={2} className={inputCls} value={form.notes || ""} onChange={u("notes")} /></Field>
        </div>
        <div className="flex items-center justify-between mt-8">
          {isEdit ? (
            <button type="button" onClick={remove} className="text-rose-400 text-sm inline-flex items-center gap-1.5"><Trash2 className="w-4 h-4" /> Delete</button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
            <PrimaryButton type="submit" disabled={uploading} data-testid="exp-submit">{uploading ? "Uploading…" : (isEdit ? "Save" : "Add")}</PrimaryButton>
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default function Finance() {
  const [tab, setTab] = useState("revenue");
  const [revenue, setRevenue] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openR, setOpenR] = useState(false);
  const [editR, setEditR] = useState(null);
  const [openE, setOpenE] = useState(false);
  const [editE, setEditE] = useState(null);
  const [range, setRange] = useState(DEFAULT_RANGE);

  const load = async () => {
    const [r, e, c, p] = await Promise.all([
      api.get("/revenue", { params: { range } }),
      api.get("/expenses", { params: { range } }),
      api.get("/clients"),
      api.get("/projects"),
    ]);
    setRevenue(r.data); setExpenses(e.data); setClients(c.data); setProjects(p.data);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [r, e, c, p] = await Promise.all([
        api.get("/revenue", { params: { range } }),
        api.get("/expenses", { params: { range } }),
        api.get("/clients"),
        api.get("/projects"),
      ]);
      if (cancelled) return;
      setRevenue(r.data); setExpenses(e.data); setClients(c.data); setProjects(p.data);
    })();
    return () => { cancelled = true; };
  }, [range]);

  useEffect(() => {
    const a = sessionStorage.getItem("equinox_quick_action");
    if (a === "expense") {
      sessionStorage.removeItem("equinox_quick_action");
      setEditE(null); setOpenE(true); setTab("expenses");
    } else if (a === "revenue") {
      sessionStorage.removeItem("equinox_quick_action");
      setEditR(null); setOpenR(true); setTab("revenue");
    }
  }, []);

  const totals = useMemo(() => {
    const tr = revenue.reduce((s, r) => s + (r.amount || 0), 0);
    const te = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    return { totalRev: tr, totalExp: te, profit: tr - te, margin: tr ? Math.round(((tr - te) / tr) * 100) : 0 };
  }, [revenue, expenses]);

  return (
    <div data-testid="finance-page">
      <PageHeader
        title="Finance"
        subtitle="Revenue, expenses, profit — the ledger of Equinox."
        testId="finance-header"
        action={
          <div className="flex items-center gap-2.5 flex-wrap">
            <RangeSelect value={range} onChange={setRange} testIdPrefix="finance-range" />
            <ExportButton endpoint={tab === "revenue" ? "/exports/revenue" : "/exports/expenses"} range={range} testId="finance-export-btn" label={`Export ${tab}`} />
            <GhostButton onClick={() => { setEditE(null); setOpenE(true); }} data-testid="new-expense-btn"><Plus className="w-4 h-4" /> Expense</GhostButton>
            <PrimaryButton onClick={() => { setEditR(null); setOpenR(true); }} data-testid="new-revenue-btn"><Plus className="w-4 h-4" /> Revenue</PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-5 mb-8">
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Revenue</div><div className="font-display text-3xl">{fmtMoney(totals.totalRev)}</div></GlassCard>
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Expenses</div><div className="font-display text-3xl">{fmtMoney(totals.totalExp)}</div></GlassCard>
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Net Profit</div><div className="font-display text-3xl">{fmtMoney(totals.profit)}</div></GlassCard>
        <GlassCard><div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">Margin</div><div className="font-display text-3xl">{totals.margin}%</div></GlassCard>
      </div>

      <div className="flex gap-2 mb-5">
        {["revenue", "expenses"].map((t) => (
          <button
            key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-full text-sm font-medium ${tab === t ? "bg-[#007AFF] text-white shadow-[0_0_18px_rgba(0,122,255,0.4)]" : "bg-white/5 text-white/60 hover:bg-white/10"}`}
          >
            {t === "revenue" ? "Revenue" : "Expenses"}
          </button>
        ))}
      </div>

      <GlassCard>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            {tab === "revenue" ? (
              <>
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Client</th>
                    <th className="py-3 pr-4">Type</th>
                    <th className="py-3 pr-4">Status</th>
                    <th className="py-3 pr-4">Notes</th>
                    <th className="py-3 pr-4 text-right">Amount</th>
                    <th className="py-3 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {revenue.map((r) => (
                    <tr key={r.id} className="hover:bg-white/[0.03]">
                      <td className="py-3 pr-4 text-white/60">{fmtDate(r.date)}</td>
                      <td className="py-3 pr-4">{r.client_name}</td>
                      <td className="py-3 pr-4"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#007AFF]/10 text-sky-300 border border-[#007AFF]/20">{r.revenue_type}</span></td>
                      <td className="py-3 pr-4 text-white/60">{r.status}</td>
                      <td className="py-3 pr-4 text-white/60">{r.notes}</td>
                      <td className="py-3 pr-4 text-right text-emerald-300 font-medium">{fmtMoney(r.amount)}</td>
                      <td className="py-3 pr-4 text-right whitespace-nowrap">
                        <button onClick={() => { setEditR(r); setOpenR(true); }} className="text-white/55 hover:text-white p-1.5" data-testid={`revenue-edit-${r.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                  {revenue.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-white/40">No revenue yet.</td></tr>}
                </tbody>
              </>
            ) : (
              <>
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4">Category</th>
                    <th className="py-3 pr-4">Vendor</th>
                    <th className="py-3 pr-4 text-right">Amount</th>
                    <th className="py-3 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {expenses.map((e) => (
                    <tr key={e.id} className="hover:bg-white/[0.03]">
                      <td className="py-3 pr-4 text-white/60">{fmtDate(e.date)}</td>
                      <td className="py-3 pr-4">{e.expense_name}</td>
                      <td className="py-3 pr-4"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-400/10 text-rose-300 border border-rose-400/20">{e.category}</span></td>
                      <td className="py-3 pr-4 text-white/60">{e.vendor}</td>
                      <td className="py-3 pr-4 text-right text-rose-300 font-medium">−{fmtMoney(e.amount)}</td>
                      <td className="py-3 pr-4 text-right">
                        <button onClick={() => { setEditE(e); setOpenE(true); }} className="text-white/55 hover:text-white p-1.5" data-testid={`expense-edit-${e.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                  {expenses.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-white/40">No expenses yet.</td></tr>}
                </tbody>
              </>
            )}
          </table>
        </div>
      </GlassCard>

      <RevenueDialog open={openR} initial={editR} clients={clients} projects={projects} onClose={() => { setOpenR(false); setEditR(null); }} onSaved={load} />
      <ExpenseDialog open={openE} initial={editE} onClose={() => { setOpenE(false); setEditE(null); }} onSaved={load} />
    </div>
  );
}
